import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, AlertTriangle, LogOut, ShieldCheck, RefreshCcw } from 'lucide-react';
import {
    getMyBlockingStatus,
    updateTaskStatus,
} from '../../api/Employee/onboardingProcesses';
import type { MyBlockingStatusView, OnboardingProcessTaskView } from '../../models/employee';
import { ProcessTaskStatus } from '../../models/employee';
import { getMyTermsStatus } from '../../api/General/termsAndPolicies';
import type { MyTermsStatusView } from '../../models/general';
import { logout } from '../../utils/AuthService';
import { clearOnboardingGateCache } from '../../components/OnboardingGate';

export default function MyOnboardingPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [status, setStatus] = useState<MyBlockingStatusView | null>(null);
    const [terms, setTerms] = useState<MyTermsStatusView | null>(null);
    const [loading, setLoading] = useState(true);
    const [busyTaskId, setBusyTaskId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [blk, trm] = await Promise.all([getMyBlockingStatus(), getMyTermsStatus()]);
            setStatus(blk.payload);
            setTerms(trm.payload);
            if (!blk.payload?.isBlocked) {
                // no longer blocked → return to dashboard.
                // Drop the gate's cached `blocked=true` so it doesn't bounce
                // the user straight back here on the next route change.
                clearOnboardingGateCache();
                navigate('/', { replace: true });
            }
        } catch {
            setError(t('Failed to load your onboarding status'));
        } finally {
            setLoading(false);
        }
    }, [navigate, t]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleMarkDone = async (task: OnboardingProcessTaskView) => {
        if (task.isPolicyAcceptanceTask) {
            navigate('/terms-and-policies/accept');
            return;
        }
        setBusyTaskId(task.id);
        try {
            await updateTaskStatus(task.id, { status: ProcessTaskStatus.Completed });
            await refresh();
        } finally {
            setBusyTaskId(null);
        }
    };

    const renderTask = (task: OnboardingProcessTaskView, blocking: boolean) => {
        const isPolicy = task.isPolicyAcceptanceTask;
        const canSelfAct = task.assigneeId ? true : true; // employee can self-complete tasks not assigned to others
        return (
            <div
                key={task.id}
                data-testid={`onboarding-task-row-${task.id}`}
                className={`flex items-start gap-3 p-4 border rounded-lg ${blocking ? 'border-rose-200 bg-rose-50' : 'border-border bg-white'}`}
            >
                <div className="pt-0.5">
                    {blocking ? <AlertTriangle className="w-5 h-5 text-rose-600" /> : <Clock className="w-5 h-5 text-gray-400" />}
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <div className="font-medium text-sm">{task.name}</div>
                        {blocking && <span data-testid="blocking-badge" className="text-xs px-2 py-0.5 bg-rose-600 text-white rounded-full">{t('Blocking')}</span>}
                        {isPolicy && <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {t('Terms & Policies')}</span>}
                    </div>
                    {task.dueDate && <div className="text-xs text-gray-500 mt-1">{t('Due')} {new Date(task.dueDate).toLocaleDateString()}</div>}
                    {task.assigneeName && <div className="text-xs text-gray-500">{t('Assigned to')}: {task.assigneeName}</div>}
                </div>
                <div>
                    {canSelfAct && (
                        <button
                            type="button"
                            onClick={() => handleMarkDone(task)}
                            disabled={busyTaskId === task.id}
                            data-testid={`onboarding-task-${task.id}-action`}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md ${isPolicy ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-primary text-white hover:opacity-90'} disabled:opacity-50`}
                        >
                            {isPolicy ? t('Read & Accept') : busyTaskId === task.id ? t('Marking…') : t('Mark as done')}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 py-10">
            <div className="max-w-2xl w-full bg-white rounded-xl shadow-sm border border-border p-6" data-testid="my-onboarding-page">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h1 className="text-xl font-semibold">{t('Complete your onboarding')}</h1>
                        <p className="text-sm text-gray-600 mt-1">
                            {t('You must complete the blocking items below before you can access the rest of the system.')}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => logout()}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                        <LogOut className="w-4 h-4" /> {t('Log out')}
                    </button>
                </div>

                {error && (
                    <div className="mt-4 p-3 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-sm">{error}</div>
                )}

                {loading && <div className="mt-6 text-sm text-gray-500">{t('Loading…')}</div>}

                {!loading && status && (
                    <div className="mt-6 space-y-6">
                        {/* Terms acceptance */}
                        {terms?.hasPublishedTerms && !terms.hasAccepted && (
                            <div className="p-4 rounded-lg border border-rose-200 bg-rose-50 flex items-start gap-3" data-testid="terms-pending-card">
                                <ShieldCheck className="w-5 h-5 text-rose-600" />
                                <div className="flex-1">
                                    <div className="font-medium text-sm">{t('Accept Terms & Policies')}</div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        {t('Your company has published new Terms & Policies (v{{v}}). You must read and accept them before continuing.', { v: terms.currentVersion })}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    data-testid="accept-terms-cta"
                                    onClick={() => navigate('/terms-and-policies/accept')}
                                    className="px-3 py-1.5 text-xs font-medium rounded-md bg-rose-600 text-white hover:bg-rose-700"
                                >
                                    {t('Read & Accept')}
                                </button>
                            </div>
                        )}

                        {/* Blocking tasks */}
                        {status.blockingTasks.length > 0 && (
                            <div>
                                <h2 className="text-sm font-semibold text-rose-700 mb-2">{t('Blocking tasks')}</h2>
                                <div className="space-y-2">
                                    {status.blockingTasks.map(task => renderTask(task, true))}
                                </div>
                            </div>
                        )}

                        {/* Non-blocking tasks */}
                        {status.nonBlockingTasks.length > 0 && (
                            <div>
                                <h2 className="text-sm font-semibold text-gray-700 mb-2">{t('Other pending tasks')}</h2>
                                <div className="space-y-2">
                                    {status.nonBlockingTasks.map(task => renderTask(task, false))}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">{t('These tasks do not block system access but should be completed.')}</p>
                            </div>
                        )}

                        {status.blockingTasks.length === 0 && !terms?.hasPublishedTerms && (
                            <div className="p-4 rounded-lg border border-green-200 bg-green-50 flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                <div>
                                    <div className="font-medium text-sm">{t('You\'re all set!')}</div>
                                    <div className="text-xs text-gray-600 mt-1">{t('No blocking items remain.')}</div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={refresh}
                                data-testid="onboarding-refresh"
                                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                            >
                                <RefreshCcw className="w-3 h-3" /> {t('Refresh status')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
